import { HttpParams } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ApiService } from '@homeleite/arch';
import { timeout } from 'rxjs/operators';

interface AuditLog {
    id: string;
    created: string;
    action: string;
    actorLogin: string | null;
    actorType: string | null;
    actorName: string | null;
    producerId: string | null;
    producerName: string | null;
    producerCode: string | null;
    controlId: string | null;
    controlStartedAt: string | null;
    controlStatus: string | null;
    milkingId: string | null;
    milkingNumber: number | null;
    animalId: string | null;
    animalNickname: string | null;
    reason: string;
    before: Record<string, unknown> | null;
    after: Record<string, unknown> | null;
    outcome: string;
    error: string | null;
    resubmitted: boolean;
}

interface Filters {
    producer: string;
    actor: string;
    animal: string;
    action: string;
    outcome: string;
    from: string;
    to: string;
    resubmitted: boolean;
}

type ViewState = 'loading' | 'error' | 'ready';

const EMPTY_FILTERS: Filters = { producer: '', actor: '', animal: '', action: '', outcome: '', from: '', to: '', resubmitted: false };

const FIELD_LABELS: Record<string, string> = {
    milkedAnimals: 'Registros de ordenha',
    milkedAnimalId: 'Registro de ordenha',
    production: 'Produção (L)',
    missingProduction: 'Motivo de falta',
    flaskNumber: 'Frasco',
    removed: 'Removido',
    nickname: 'Animal',
    animalInactivated: 'Animal inativado',
    birthdate: 'Nascimento'
};

const MISSING_LABELS: Record<string, string> = {
    IN_HEAT: 'Cio',
    MASTITIS: 'Mastite',
    NEW_BORN: 'Recém parida',
    IN_TREATMENT: 'Em tratamento',
    EXPOSITION: 'Exposição',
    DISCARD_OR_DEATH: 'Descarte/Morte'
};

interface DetailSection {
    title: string;
    rows: { label: string; value: string }[];
}

@Component({
    selector: 'homeleite-adjustments-audit',
    templateUrl: './adjustments-audit.component.html',
    styleUrls: ['./adjustments-audit.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AdjustmentsAuditComponent implements OnInit {
    readonly pageSize = 20;
    readonly actionOptions = [
        { value: 'ADD_TO_MILKING', label: 'Adicionou à ordenha' },
        { value: 'REMOVE_FROM_MILKING', label: 'Removeu da ordenha' },
        { value: 'DELETE_ANIMAL', label: 'Excluiu animal' },
        { value: 'FIX_BIRTHDATE', label: 'Informou nascimento' }
    ];
    readonly outcomeOptions = [
        { value: 'SUCCESS', label: 'Concluído', icon: 'check_circle' },
        { value: 'FAILED', label: 'Falhou', icon: 'error' },
        { value: 'STARTED', label: 'Em andamento', icon: 'schedule' }
    ];

    state: ViewState = 'loading';
    items: AuditLog[] = [];
    page = 0;
    hasMore = false;
    filters: Filters = { ...EMPTY_FILTERS };
    applied: Filters = { ...EMPTY_FILTERS };
    expanded = new Set<string>();

    constructor(private api: ApiService, private cd: ChangeDetectorRef) {}

    ngOnInit(): void {
        this.load(0);
    }

    get activeCount(): number {
        const a = this.applied;
        return [a.producer, a.actor, a.animal, a.action, a.outcome, a.from, a.to].filter((v) => v.trim()).length + (a.resubmitted ? 1 : 0);
    }

    search(): void {
        this.applied = { ...this.filters };
        this.load(0);
    }

    clear(): void {
        this.filters = { ...EMPTY_FILTERS };
        this.search();
    }

    load(page: number): void {
        this.page = page;
        this.state = 'loading';
        this.cd.markForCheck();
        const f = this.applied;
        let params = new HttpParams().set('page', page).set('size', this.pageSize);
        const text: [keyof Filters, string][] = [['producer', f.producer], ['actor', f.actor], ['animal', f.animal], ['action', f.action], ['outcome', f.outcome]];
        for (const [key, value] of text) if (value.trim()) params = params.set(key, value.trim());
        if (f.from) params = params.set('from', new Date(`${f.from}T00:00:00`).toISOString());
        if (f.to) params = params.set('to', new Date(`${f.to}T23:59:59.999`).toISOString());
        if (f.resubmitted) params = params.set('resubmitted', 'true');
        this.api.root
            .path('milk-adjustments', 'audit')
            .getJson<AuditLog[]>({ params })
            .pipe(timeout(15000))
            .subscribe({
                next: (items) => {
                    this.items = items;
                    this.hasMore = items.length === this.pageSize;
                    this.state = 'ready';
                    this.cd.markForCheck();
                },
                error: () => {
                    this.state = 'error';
                    this.cd.markForCheck();
                }
            });
    }

    actionLabel(action: string): string {
        return this.actionOptions.find((o) => o.value === action)?.label ?? action;
    }

    outcomeOf(outcome: string) {
        return this.outcomeOptions.find((o) => o.value === outcome) ?? { value: outcome, label: outcome, icon: 'help' };
    }

    actorTypeLabel(type: string | null): string {
        return type === 'ADMIN' ? 'Admin' : type === 'CONTROLLER' ? 'Controlador' : type === 'PRODUCER' ? 'Produtor' : '';
    }

    controlStatusLabel(status: string | null): string {
        return status === 'CLOSED' ? 'Fechado' : status ? 'Em andamento' : '';
    }

    summary(l: AuditLog): string {
        const animal = l.animalNickname || 'animal';
        const milking = l.milkingNumber ? ` à ${l.milkingNumber}ª ordenha` : ' à ordenha';
        switch (l.action) {
            case 'ADD_TO_MILKING': {
                const qty = this.liters(l.after?.['production']);
                const missing = this.missingLabel(l.after?.['missingProduction']);
                return `Adicionou ${animal}${milking}` + (qty ? ` · ${qty}` : missing ? ` · sem produção (${missing})` : '');
            }
            case 'REMOVE_FROM_MILKING': {
                const qty = this.liters(l.before?.['production']);
                const flask = l.before?.['flaskNumber'];
                const from = l.milkingNumber ? ` da ${l.milkingNumber}ª ordenha` : ' da ordenha';
                const extra = [qty && `tinha ${qty}`, flask != null && `frasco ${flask}`].filter(Boolean).join(', ');
                return `Removeu ${animal}${from}` + (extra ? ` (${extra})` : '');
            }
            case 'DELETE_ANIMAL': {
                const n = Number(l.before?.['milkedAnimals'] ?? 0);
                return `Excluiu ${animal}` + (n ? ` (${n} registro${n > 1 ? 's' : ''} de ordenha)` : '');
            }
            case 'FIX_BIRTHDATE': {
                const br = (v: unknown) => (typeof v === 'string' ? v.split('-').reverse().join('/') : '');
                return `Informou nascimento de ${animal} (${br(l.before?.['birthdate'])} → ${br(l.after?.['birthdate'])})`;
            }
            default:
                return this.actionLabel(l.action);
        }
    }

    sections(l: AuditLog): DetailSection[] {
        const b = l.before ?? {};
        const a = l.after ?? {};
        const done = l.outcome === 'SUCCESS';
        const animal = l.animalNickname || (b['nickname'] as string) || '-';
        const milking = (n: unknown) => (n ? `${n}ª ordenha` : null);
        const date = (v: unknown) => (typeof v === 'string' && v ? v.split('-').reverse().join('/') : null);
        const count = (from: unknown, to: unknown) => (from == null ? null : to == null ? `${from}` : `${from} → ${to}`);
        const keep = (rows: [string, string | null][]) => rows.filter((r): r is [string, string] => !!r[1]).map(([label, value]) => ({ label, value }));
        switch (l.action) {
            case 'ADD_TO_MILKING': {
                const total = a['milkedAnimals'] ?? (done && typeof b['milkedAnimals'] === 'number' ? b['milkedAnimals'] + 1 : null);
                return [{
                    title: done ? 'Incluído na ordenha' : 'Tentativa de inclusão na ordenha',
                    rows: keep([
                        ['Animal', animal],
                        ['Ordenha', milking(a['milkingNumber'] ?? l.milkingNumber)],
                        ['Data', date(a['date'])],
                        ['Produção', this.liters(a['production']) || (a['missingProduction'] ? 'Sem produção' : null)],
                        ['Motivo sem produção', this.missingLabel(a['missingProduction'])],
                        ['Frasco', this.text(a['flaskNumber'])],
                        ['Posição', this.text(a['position'])],
                        ['Animais na ordenha', count(b['milkedAnimals'], total)]
                    ])
                }];
            }
            case 'REMOVE_FROM_MILKING':
                return [{
                    title: done ? 'Retirado da ordenha' : 'Tentativa de retirada da ordenha',
                    rows: keep([
                        ['Animal', animal],
                        ['Ordenha', milking(l.milkingNumber)],
                        ['Data', date(b['date'])],
                        ['Produção que tinha', this.liters(b['production']) || (b['missingProduction'] ? 'Sem produção' : null)],
                        ['Motivo sem produção', this.missingLabel(b['missingProduction'])],
                        ['Frasco', this.text(b['flaskNumber'])],
                        ['Posição', this.text(b['position'])]
                    ])
                }];
            case 'DELETE_ANIMAL':
                return [{
                    title: done ? 'Animal excluído' : 'Tentativa de exclusão do animal',
                    rows: keep([
                        ['Animal', animal],
                        ['Registros de ordenha retirados', this.text(b['milkedAnimals'])],
                        ['Animal inativado', a['animalInactivated'] ? 'Sim' : null]
                    ])
                }];
            case 'FIX_BIRTHDATE':
                return [
                    { title: 'Antes', rows: keep([['Nascimento', date(b['birthdate']) ?? 'Não informado']]) },
                    { title: 'Depois', rows: keep([['Nascimento', date(a['birthdate']) ?? '-']]) }
                ];
            default:
                return [
                    { title: 'Antes', rows: this.entries(b) },
                    { title: 'Depois', rows: this.entries(a) }
                ];
        }
    }

    private entries(value: Record<string, unknown>): { label: string; value: string }[] {
        return Object.entries(value).map(([k, v]) => ({ label: FIELD_LABELS[k] ?? k, value: this.format(k, v) }));
    }

    private text(value: unknown): string | null {
        return value == null || value === '' ? null : String(value);
    }

    private missingLabel(value: unknown): string | null {
        return typeof value === 'string' && value ? MISSING_LABELS[value] ?? value : null;
    }

    toggle(id: string): void {
        if (!this.expanded.delete(id)) this.expanded.add(id);
        this.cd.markForCheck();
    }

    json(value: unknown): string {
        return JSON.stringify(value ?? {}, null, 2);
    }

    private liters(value: unknown): string {
        const n = Number(value);
        return value != null && value !== '' && !isNaN(n) ? `${n.toLocaleString('pt-BR')} L` : '';
    }

    private format(key: string, value: unknown): string {
        if (value == null || value === '') return '-';
        if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
        if (key === 'production') return this.liters(value) || String(value);
        return typeof value === 'object' ? JSON.stringify(value) : String(value);
    }
}
